// Per-date coach-session target-vs-actual (0027).
//
// A coach prescription is per-DATE with its own exercise list + explicit
// per-set weights, so this surface compares each APPLY-d coach strength session
// against the actuals logged THAT day — including free-form / bodyweight
// exercises that never produce device actuals.
//
// Shared by the session inspector (that day's session) and the TRENDS
// by-muscle panel (recent sessions) so both render coach sessions identically.

import { C, FONT_DISPLAY, FONT_MONO, SUBLABEL_TRACKING } from '../lib/design';
import {
  formatSetChip,
  formatTargetSetChip,
  type CoachSessionComparison as CoachSessionComparisonData,
} from '../lib/speediance-feedback';

function dateLabel(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  try {
    return new Date(y, m - 1, d)
      .toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' })
      .toUpperCase();
  } catch {
    // iso is already YYYY-MM-DD — fall back to it verbatim.
    return iso;
  }
}

function targetSeries(sets: CoachSessionComparisonData['exercises'][number]['prescribedSetDetails']): string {
  const labels = sets.map((set) => formatTargetSetChip(
    set.reps ?? 0,
    set.weight_kg,
    false,
    set.duration_s ?? null,
  ));
  if (labels.length > 1 && labels.every((label) => label === labels[0])) {
    return `${labels.length} sets · ${labels[0]}`;
  }
  return labels.join(' · ') || '—';
}

function actualSeries(actual: NonNullable<CoachSessionComparisonData['exercises'][number]['actual']>): string {
  const labels = actual.sets.map(formatSetChip);
  if (labels.length > 1 && labels.every((label) => label === labels[0])) {
    return `${labels.length} sets · ${labels[0]}`;
  }
  return labels.join(' · ') || '—';
}

export type CoachSessionComparisonProps = {
  sessions: CoachSessionComparisonData[];
  /** Show at most this many recent sessions (default 4). */
  limit?: number;
};

export function CoachSessionComparison({ sessions, limit = 4 }: CoachSessionComparisonProps) {
  if (sessions.length === 0) return null;
  // `sessions` is ascending by date; show the most RECENT `limit` of them while
  // keeping that ascending order (take the tail, don't slice from the front —
  // that would keep the oldest and drop today's session).
  const shown = sessions.slice(-limit);

  return (
    <div className="space-y-3">
      {shown.map((s) => (
        <div key={s.date} style={{ background: C.panel2, border: `1px solid ${C.line}` }} className="p-4">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              marginBottom: 12,
              gap: 10,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0 }}>
              <span
                style={{
                  color: C.signal,
                  fontSize: 9,
                  letterSpacing: SUBLABEL_TRACKING,
                  fontFamily: FONT_MONO,
                  fontWeight: 700,
                }}
              >
                COACH
              </span>
              <span style={{ fontFamily: FONT_DISPLAY, fontSize: 15, color: C.text }}>
                {s.title}
              </span>
            </div>
            <span
              className="num"
              style={{ color: C.faint, fontSize: 9, letterSpacing: SUBLABEL_TRACKING, whiteSpace: 'nowrap' }}
            >
              {dateLabel(s.date)}
            </span>
          </div>

          {/* A bodyweight completion confirms the session, but reps/durations
              remain unknown unless an actual source captured them. */}
          {s.bodyweight && s.completed && !s.hasAnyActuals ? (
            <div
              style={{
                color: C.signal,
                fontSize: 10,
                letterSpacing: SUBLABEL_TRACKING,
                fontFamily: FONT_MONO,
                fontWeight: 700,
                marginBottom: 8,
              }}
            >
              DONE · BODYWEIGHT · ACTUAL REPS/TIME NOT CAPTURED
            </div>
          ) : (
            !s.hasAnyActuals && (
              <div
                style={{
                  color: C.faint,
                  fontSize: 10,
                  letterSpacing: SUBLABEL_TRACKING,
                  fontFamily: FONT_MONO,
                  marginBottom: 8,
                }}
              >
                NOT YET LOGGED
              </div>
            )
          )}

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr auto auto',
              gap: 10,
              paddingBottom: 4,
              fontSize: 9,
              letterSpacing: SUBLABEL_TRACKING,
              color: C.faint,
            }}
          >
            <div>EXERCISE</div>
            <div style={{ textAlign: 'right' }}>TARGET</div>
            <div style={{ textAlign: 'right' }}>ACTUAL</div>
          </div>
          <div className="mb-[3px] text-right text-[8px]" style={{ color: C.faint }}>
            PRESCRIPTION → SPEEDIANCE / MANUAL ACTUALS
          </div>

          {s.exercises.map((ex, i) => {
            const targetText = targetSeries(ex.prescribedSetDetails);
            const actualChip = ex.actual
              ? actualSeries(ex.actual)
              : s.bodyweight && s.completed
                ? 'DONE · NOT CAPTURED'
                : '—';
            return (
              <div
                key={`${ex.name}-${i}`}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto auto',
                  gap: 10,
                  padding: '6px 0',
                  borderTop: `1px solid ${C.line}`,
                  fontSize: 11,
                  alignItems: 'baseline',
                }}
              >
                <div style={{ color: C.text, minWidth: 0 }}>{ex.name}</div>
                <div className="num" style={{ textAlign: 'right', color: C.dim, whiteSpace: 'nowrap' }}>
                  {targetText}
                </div>
                <div
                  className="num"
                  style={{
                    textAlign: 'right',
                    color: ex.actual ? C.signal : s.bodyweight && s.completed ? C.warn : C.faint,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {actualChip}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
